import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { FacebookShareButton, LinkedinShareButton } from "react-share";
import { GET_IMAGE } from "../../helpers/images";
import { GET_USER_KPIS } from "../../api/kpi";
import { ADD_COMMENT, UPDATE_COMMENT, DELETE_COMMENT, FETCH_COMMENTS } from "../../api/comments";
import { ADD_REACT, DELETE_REACT, FETCH_REACT } from "../../api/react";
import { UseCompanyId, UseUserDetails } from "../../hooks/auth";
import { UseCompanyKpis } from "../../hooks/kpi";
import { UseCompanyColors, UseUsersDictionary } from "../../hooks/company";
import { recomputeEntryPts, indexKpisById } from "../../helpers/kpiPoints";
import SlowClap from "../../components/SlowClap/SlowClap";
import CommentList from "../../components/Comments/CommentList";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import ShareSheet from "./ShareSheet";
import { shareToast } from "./ShareToast";
import { REACTION_PROPERTY_TYPE } from "../../constants/reactions";
import { TOKENS, FONT, NUM_FEATURES } from "./feedTokens";
import socket from "../../configs/socket";
import socketConstants from "../../constants/socket";

const { SOCKET_EVENT } = socketConstants;

/** Fence glyph used for the Yard Sign identity (lucide-style). */
const Fence = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 3v18" /><path d="M20 3v18" /><path d="M4 8h16" /><path d="M4 14h16" /><path d="M9 3v18" /><path d="M14 3v18" />
  </svg>
);

/** Format points without forcing integers (a sign can be worth e.g. 0.2 pts). */
const fmtPts = (n) => String(Math.round((Number(n) || 0) * 100) / 100);
const dayKeyFor = (createdAt) => moment(createdAt).format("YYYY-MM-DD");

const DAY_MS = 24 * 60 * 60 * 1000;
const objectIdTime = (id) => {
  const s = String(id || "");
  return /^[0-9a-f]{24}$/i.test(s) ? parseInt(s.slice(0, 8), 16) * 1000 : null;
};

/**
 * When the sign was actually saved. From 5 PM PT the server pins createdAt
 * to noon (+ up to an hour) of the company's day so the entry stays in that
 * day's scores — every evening sign then read "12:xx PM", sank below the
 * afternoon's posts and landed mid-strip in its own group. Prefer the
 * client's takenAt, else the record id's creation time when it falls inside
 * that shift (a past-month add pinned to month end keeps its createdAt).
 */
const signTimeOf = (r) => {
  const taken = Date.parse(r?.metadata?.takenAt);
  if (Number.isFinite(taken)) return moment(taken);
  const created = moment(r.createdAt);
  const idTime = objectIdTime(r._id);
  const shift = idTime == null ? -1 : idTime - created.valueOf();
  return shift >= 0 && shift < DAY_MS ? moment(idTime) : created;
};

// Signs older than the Feed's loaded jobs are never shown, so the first load
// asks for a window instead of every sign the company ever posted (Kitsap:
// ~4,700 rows / 1.6 MB, re-sent after every save). Refreshes after a save
// only re-ask for the last couple of days and splice them in.
const YARD_WINDOW_DAYS = 60;
const YARD_REFRESH_DAYS = 2;
const dayLabelFor = (m) => {
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("MMM D");
};

/** "a few mins ago" / "1 hour ago" today, "Yesterday", then the actual date
    ("Aug 11") — a bare weekday name ("Tuesday") hid the date entirely. */
const relativeLabelFor = (m) => {
  const now = moment();
  const diffMin = now.diff(m, "minutes");
  const diffHr = now.diff(m, "hours");
  if (diffMin < 5) return "Just now";
  if (diffMin < 60) return `${diffMin} mins ago`;
  if (m.isSame(now, "day")) return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format(m.isSame(now, "year") ? "MMM D" : "MMM D, YYYY");
};

const mapsUrl = (lat, lng, address) =>
  (lat && lng)
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || "")}`;

/**
 * Yard Sign KPI submissions grouped into one entry per (player, day) — a
 * same-day submission joins its player's existing group (updated photos +
 * total) instead of creating a new one. Consumed by FeedPage, which merges
 * these groups into the same chronological list as regular job posts —
 * Yard Signs are not a separate section, just another post type sorted by
 * activity time alongside everything else.
 */
export function useYardSignGroups(oldestShown) {
  const companyKpis = UseCompanyKpis();
  const companyId = UseCompanyId();
  const usersDict = UseUsersDictionary();
  const [records, setRecords] = useState([]);

  // Every KPI id (across groups) whose title is a yard sign.
  const yardIds = useMemo(() => {
    const set = new Set();
    Object.values(companyKpis || {}).forEach((arr) => {
      (arr || []).forEach((k) => {
        if ((k.title || "").toLowerCase().includes("yard")) set.add(k._id);
      });
    });
    return set;
  }, [companyKpis]);

  // Changes whenever any player's Yard Sign month total moves (a new photo was
  // just saved here, or the scoreboard refreshed). Re-fetching on it keeps a
  // fresh sign from missing the feed: the list used to load once on mount, so a
  // sign saved a moment later only appeared after a manual reload.
  const yardScoreKey = useSelector((state) =>
    (state.kpiReducer.usersKpis || [])
      .map((u) => [...yardIds].map((id) => u?.month?.[id]?.amt || 0).join(","))
      .join("|"),
  );

  // Window start, widened a month at a time as older job pages load.
  const windowStart = useMemo(() => {
    const floor = moment().subtract(YARD_WINDOW_DAYS, "days");
    const oldest = oldestShown ? moment(oldestShown) : null;
    const start = oldest?.isValid() && oldest.isBefore(floor) ? oldest : floor;
    return start.clone().startOf("month").format("YYYY-MM-DD");
  }, [oldestShown]);

  const fetchSince = (startDate) =>
    Promise.all(
      [...yardIds].map((id) =>
        GET_USER_KPIS(companyId, null, id, { startDate }).then((r) => r?.data).catch(() => null))
    ).then((results) => {
      // Any failed call → null, so a refresh never wipes signs it couldn't re-read.
      if (results.some((d) => d == null)) return null;
      return results.flatMap((d) => (Array.isArray(d) ? d : (d?.data || d?.kpis || [])));
    });

  // Full load for the window (one call per yard KPI id).
  // Scoreboard key the loaded records reflect; a newer key means re-read.
  const [loadedKey, setLoadedKey] = useState(null);
  useEffect(() => {
    if (!companyId || !yardIds.size) return undefined;
    let cancelled = false;
    const key = yardScoreKey;
    fetchSince(windowStart).then((all) => {
      if (cancelled || !all) return;
      setRecords(all);
      setLoadedKey(key);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yardIds, windowStart]);

  // A save moved the scoreboard: re-read only the last few days and splice
  // them over the same days in what's loaded (also drops deleted signs).
  useEffect(() => {
    if (!companyId || !yardIds.size || loadedKey == null) return undefined;
    if (loadedKey === yardScoreKey) return undefined;
    let cancelled = false;
    const key = yardScoreKey;
    const since = moment().subtract(YARD_REFRESH_DAYS, "days").format("YYYY-MM-DD");
    const cutoff = moment.utc(since).startOf("day").valueOf();
    fetchSince(since).then((recent) => {
      if (cancelled || !recent) return;
      setRecords((prev) => [
        ...prev.filter((r) => moment(r.createdAt).valueOf() < cutoff),
        ...recent,
      ]);
      setLoadedKey(key);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yardIds, yardScoreKey, loadedKey]);

  // Group signs into one entry per (player, day), newest activity first —
  // a fresh same-day submission bumps its group and its running total.
  return useMemo(() => {
    // Recompute each sign's pts from the owning KPI's workspace config —
    // same rule as FeedCard / leaderboard / Scorekeeper (amount ×
    // pointValue/pointScale) — instead of trusting the server-stored pts,
    // which go stale when the point rule changes and made the group badge
    // disagree with every other points surface (e.g. +1.6 for 8 signs).
    // Falls back to the stored pts when the KPI config isn't loaded.
    const kpiById = indexKpisById(Object.values(companyKpis || {}).flat());
    const ptsOf = (r) => recomputeEntryPts(r, kpiById[r.kpiId]);

    const buckets = new Map();
    records
      .filter((r) => r?.metadata?.photo && r?.ownerId)
      .forEach((r) => {
        const key = `${r.ownerId}_${dayKeyFor(signTimeOf(r))}`;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(r);
      });

    return [...buckets.entries()].map(([key, entries]) => {
      const sorted = [...entries].sort((a, b) => signTimeOf(b).valueOf() - signTimeOf(a).valueOf());
      const latest = sorted[0];
      const u = usersDict?.[latest.ownerId] || usersDict?.[latest.userId] || usersDict?.[latest.reporterId] || {};
      const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Teammate";
      const totalPoints = sorted.reduce((s, r) => s + ptsOf(r), 0);
      const signs = sorted.map((r) => {
        const addr = r.metadata.address || "";
        const m = signTimeOf(r);
        return {
          id: r._id,
          img: GET_IMAGE(r.metadata.photo),
          address: addr,
          area: (addr.split(",")[0] || "").trim() || "Yard sign",
          time: m.format("h:mm A"),
          relative: relativeLabelFor(m),
          when: `${dayLabelFor(m)} · ${m.format("h:mm A")}`,
          lat: r.metadata.lat,
          lng: r.metadata.lng,
          points: ptsOf(r),
        };
      });
      return {
        id: `yardsign_${key}`,
        // The most recent submission's record id — comments/reacts for the
        // whole (player, day) group hang off this real backend id.
        postId: latest._id,
        ownerId: latest.ownerId,
        name,
        initials: (`${(u.firstName || "")[0] || ""}${(u.lastName || "")[0] || ""}`).toUpperCase() || "•",
        avatar: u.image ? GET_IMAGE(u.image) : null,
        // Raw (untransformed) image key — SoftProfile calls GET_IMAGE itself.
        avatarRaw: u.image || null,
        totalPoints,
        signs,
        timestamp: signTimeOf(latest).toISOString(),
      };
    });
  }, [records, usersDict, companyKpis]);
}

/**
 * One player's Yard Sign activity for one day, rendered as a normal Feed
 * post — same card rhythm as FeedCard, sitting inline in the main list.
 * The only carousel here is the photo track *within* this single post
 * (dot-paginated, one sign per slide) when the player placed more than one
 * sign that day. It never swipes between different players' posts.
 */
export default function YardSignPost({ group, elementId, autoOpenComments }) {
  const { primaryColor } = UseCompanyColors();
  const brand = primaryColor || "#1B598E";
  const currentUser = UseUserDetails();
  const trackRef = useRef(null);
  const lightboxTrackRef = useRef(null);
  const pendingReactAdds = useRef({});
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const onTrackScroll = () => {
    const t = trackRef.current;
    if (!t) return;
    const idx = Math.round(t.scrollLeft / t.clientWidth);
    if (idx !== photoIdx) setPhotoIdx(idx);
  };
  const goToPhoto = (delta) => {
    const t = trackRef.current;
    if (!t) return;
    const nextIdx = Math.min(Math.max(photoIdx + delta, 0), group.signs.length - 1);
    t.scrollTo({ left: nextIdx * t.clientWidth, behavior: "smooth" });
    setPhotoIdx(nextIdx);
  };
  const current = group.signs[photoIdx] || group.signs[0];
  const active = lightboxIdx != null ? group.signs[lightboxIdx] : null;

  /* Jump the lightbox track to whichever slide was tapped as soon as it
     mounts — scroll-snap alone won't land on the right slide on open. */
  useEffect(() => {
    if (lightboxIdx == null) return;
    const t = lightboxTrackRef.current;
    if (!t) return;
    t.scrollTo({ left: lightboxIdx * t.clientWidth, behavior: "instant" });
  }, [lightboxIdx != null]);

  const onLightboxTrackScroll = () => {
    const t = lightboxTrackRef.current;
    if (!t) return;
    const idx = Math.round(t.scrollLeft / t.clientWidth);
    if (idx !== lightboxIdx) setLightboxIdx(idx);
  };

  // The lightbox track only had scroll-snap to rely on (swipe/drag) — fine
  // on mobile, but a desktop mouse user had no way to advance photos at all.
  const goToLightboxPhoto = (delta) => {
    const t = lightboxTrackRef.current;
    if (!t || lightboxIdx == null) return;
    const nextIdx = Math.min(Math.max(lightboxIdx + delta, 0), group.signs.length - 1);
    t.scrollTo({ left: nextIdx * t.clientWidth, behavior: "smooth" });
    setLightboxIdx(nextIdx);
  };

  useEffect(() => {
    if (lightboxIdx == null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goToLightboxPhoto(-1);
      else if (e.key === "ArrowRight") goToLightboxPhoto(1);
      else if (e.key === "Escape") setLightboxIdx(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx]);

  /* ---- Claps / comments (hung off the latest submission's record id) ---- */
  const [reacts, setReacts] = useState([]);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(!!autoOpenComments);

  useEffect(() => {
    let cancelled = false;
    FETCH_REACT(group.postId)
      .then((r) => {
        if (cancelled) return;
        const data = r?.data;
        const candidates = [data, data?.reacts, data?.data];
        setReacts(candidates.find((c) => Array.isArray(c)) || []);
      })
      .catch(() => {});
    FETCH_COMMENTS(group.postId, 1)
      .then((r) => { if (!cancelled) setComments(r?.data?.comments || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [group.postId]);

  const onReactToggle = async (propertyId, currentUserId, isActive, react, iconType, propertyModel) => {
    // Optimistic UI: reflect the tap instantly instead of waiting on the
    // ADD_REACT/DELETE_REACT round trip to paint.
    if (isActive) {
      const tempId = `temp_${currentUserId}_${Date.now()}`;
      setReacts((prev) => [...prev, { _id: tempId, users: [{ _id: currentUserId }] }]);
      if (propertyModel === REACTION_PROPERTY_TYPE.COMMENT) {
        socket.emit(SOCKET_EVENT.POST_COMMENT_LIKE, {
          sender: currentUserId,
          commentId: propertyId,
          targetId: group.postId,
          targetType: "EmployeeKpi",
        });
      } else {
        socket.emit(SOCKET_EVENT.POST_SLOW_CLAP, {
          sender: currentUserId,
          targetId: group.postId,
          targetType: "EmployeeKpi",
          recipientIds: [group.ownerId],
        });
      }
      const addPromise = ADD_REACT({ propertyId, data: { userId: currentUserId, propertyModel, iconType } }).then(
        ({ data: added }) => {
          setReacts((prev) => prev.map((r) => (r._id === tempId ? added : r)));
          return added;
        },
      );
      pendingReactAdds.current[tempId] = addPromise;
      try {
        await addPromise;
      } catch (err) {
        setReacts((prev) => prev.filter((r) => r._id !== tempId));
        toast.error("Couldn't update reaction");
      } finally {
        delete pendingReactAdds.current[tempId];
      }
    } else {
      setReacts((prev) => prev.filter((r) => r._id !== react._id));
      try {
        // If this reaction hasn't finished being created yet (rapid tap-off),
        // wait for the real id instead of deleting the temporary local one.
        const pendingAdd = pendingReactAdds.current[react._id];
        const realId = pendingAdd ? (await pendingAdd)._id : react._id;
        await DELETE_REACT(realId);
      } catch (err) {
        setReacts((prev) => [...prev, react]);
        toast.error("Couldn't update reaction");
      }
    }
  };

  const onAddComment = async (propertyId, senderId, content) => {
    try {
      const { data: comment } = await ADD_COMMENT({
        jobId: propertyId,
        data: { userId: senderId, content, propertyModel: REACTION_PROPERTY_TYPE.EMPLOYEE_KPI },
      });
      setComments((prev) => [...prev, comment]);
      socket.emit(SOCKET_EVENT.POST_COMMENT, {
        sender: senderId,
        targetId: group.postId,
        targetType: "EmployeeKpi",
        content,
        recipientIds: [group.ownerId],
      });
    } catch (err) {
      toast.error("Couldn't post comment");
    }
  };

  const onEditComment = async (commentId, content) => {
    try {
      const { data: updated } = await UPDATE_COMMENT(commentId, content);
      setComments((prev) => prev.map((c) => (c._id === commentId ? updated : c)));
    } catch (err) {
      toast.error("Couldn't update comment");
    }
  };

  const onDeleteComment = async (commentId) => {
    try {
      await DELETE_COMMENT(commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
      toast.error("Couldn't delete comment");
    }
  };

  /* ---- Share (uses the sign's own photo directly — no before/after
     compositing pipeline, that's a Job-specific concept) ---- */
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareBtnRef = useRef(null);
  const fbBtnRef = useRef(null);
  const liBtnRef = useRef(null);
  const dlBtnRef = useRef(null);
  const shareImg = current?.img || "";

  const handleShareFacebook = () => {
    setShareMenuOpen(false);
    setTimeout(() => fbBtnRef.current?.click(), 0);
  };
  const handleShareLinkedIn = () => {
    setShareMenuOpen(false);
    setTimeout(() => liBtnRef.current?.click(), 0);
  };
  const handleCopyLink = async () => {
    setShareMenuOpen(false);
    try {
      await navigator.clipboard.writeText(shareImg);
      shareToast.success("Link copied", "Ready to paste and share.");
    } catch (err) {
      shareToast.error("Couldn't copy link", "Please try again.");
    }
  };
  const handleDownload = () => {
    setShareMenuOpen(false);
    dlBtnRef.current?.click();
  };

  return (
    <div
      id={elementId}
      style={{
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset, 0 1px 2px rgba(15,23,42,0.04), 0 18px 36px rgba(15,23,42,0.07)",
        marginBottom: "10px",
        fontFamily: FONT,
        color: TOKENS.ink,
      }}
    >
      {/* HEADER — same layout/rhythm as a regular feed post: avatar + name/tag
          on the left, points + relative time stacked on the right */}
      <div
        className="flex items-center"
        style={{
          gap: "14px",
          padding: "18px 22px 14px",
          background: "linear-gradient(180deg, #F6F9FF 0%, #FFFFFF 100%)",
          borderBottom: `1px solid ${TOKENS.hairline}`,
        }}
      >
        <div style={{ flexShrink: 0, boxShadow: "0 0 0 2px #fff, 0 0 0 3px rgba(37,99,235,0.18)", borderRadius: "50%" }}>
          <SoftProfile src={group.avatarRaw} name={group.name} size="md" />
        </div>
        <div className="flex-1 min-w-0">
          <div style={{ fontWeight: 600, fontSize: "15px", letterSpacing: "-0.01em", color: TOKENS.ink, lineHeight: 1.2 }}>
            {group.name}
          </div>
          <span
            className="inline-flex items-center"
            style={{
              marginTop: "6px",
              gap: "5px",
              height: "18px",
              padding: "0 8px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.01em",
              background: TOKENS.primarySoft,
              color: TOKENS.primary,
            }}
          >
            <Fence size={9} /> Yard sign
          </span>
        </div>
        <div className="flex flex-col items-end flex-shrink-0" style={{ gap: "4px" }}>
          {Math.round(group.totalPoints) > 0 && (
            <div
              style={{
                fontFamily: FONT,
                fontFeatureSettings: NUM_FEATURES,
                display: "inline-flex",
                alignItems: "baseline",
                gap: "4px",
                background: "linear-gradient(180deg, #16A34A 0%, #15803D 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              <span style={{ fontSize: "15px", fontWeight: 600 }}>+{Math.round(group.totalPoints)}</span>
              <span style={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.02em" }}>pts</span>
            </div>
          )}
          <div style={{ fontSize: "11px", color: TOKENS.muted, fontWeight: 500 }}>{current?.relative}</div>
        </div>
      </div>

      {/* PHOTO — inset + rounded, matching a regular feed post's photo treatment */}
      <div className="group" style={{ position: "relative", margin: "12px 16px 0", borderRadius: "12px", overflow: "hidden" }}>
        <div
          ref={trackRef}
          onScroll={onTrackScroll}
          className="flex overflow-x-auto"
          style={{ scrollSnapType: "x mandatory", scrollBehavior: "smooth", scrollbarWidth: "none" }}
        >
          {group.signs.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setLightboxIdx(i)}
              className="relative flex-shrink-0 w-full"
              style={{ scrollSnapAlign: "start", aspectRatio: "16 / 9", maxHeight: "360px" }}
            >
              <img src={s.img} alt="Yard sign" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>

        {/* Prev/next arrows — desktop-only, hover-revealed. Mobile relies on
            the swipe/scroll-snap track above instead. */}
        {group.signs.length > 1 && photoIdx > 0 && (
          <button
            type="button"
            aria-label="Previous sign"
            onClick={() => goToPhoto(-1)}
            className="hidden sm:flex absolute top-1/2 left-[12px] -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(11,19,32,0.55)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff",
              zIndex: 5,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        {group.signs.length > 1 && photoIdx < group.signs.length - 1 && (
          <button
            type="button"
            aria-label="Next sign"
            onClick={() => goToPhoto(1)}
            className="hidden sm:flex absolute top-1/2 right-[12px] -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(11,19,32,0.55)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#fff",
              zIndex: 5,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        )}

        {/* Dot pagination — one sign per slide, scoped to this post only */}
        {group.signs.length > 1 && (
          <div className="absolute bottom-[10px] left-1/2 -translate-x-1/2 flex items-center gap-[5px]">
            {group.signs.map((s, i) => (
              <span
                key={s.id}
                className="rounded-full transition-[width] duration-200"
                style={{
                  width: i === photoIdx ? 14 : 6,
                  height: 6,
                  background: i === photoIdx ? "#fff" : "rgba(255,255,255,0.55)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* META STRIP — address, same treatment as a regular feed post's meta strip */}
      <div
        style={{
          padding: "10px 22px",
          background: TOKENS.surface2,
          borderBottom: `1px solid ${TOKENS.hairline}`,
          fontSize: "12px",
          color: TOKENS.muted,
          fontWeight: 500,
        }}
        className="truncate"
      >
        {current?.area}
        {group.signs.length > 1 && (
          <span style={{ color: TOKENS.faint }}> · {photoIdx + 1}/{group.signs.length}</span>
        )}
      </div>

      {/* clap / comment / share bar — same footer as a regular feed post */}
      <div className="flex items-center gap-1 relative" style={{ padding: "8px 12px", borderTop: `1px solid ${TOKENS.hairline}` }}>
        <SlowClap
          jobId={group.postId}
          currentUserId={currentUser?._id}
          reacts={reacts}
          onToggle={onReactToggle}
          showDetails={false}
          propertyModel={REACTION_PROPERTY_TYPE.EMPLOYEE_KPI}
        />
        <button
          type="button"
          onClick={() => setShowComments((v) => !v)}
          className={`inline-flex items-center hover:bg-[#F8FAFD] ${showComments ? "bg-[#F8FAFD]" : "bg-transparent"}`}
          style={{
            gap: "8px",
            height: "38px",
            padding: "0 14px",
            borderRadius: "10px",
            border: "none",
            cursor: "pointer",
            fontFamily: FONT,
            fontSize: "13px",
            fontWeight: 600,
            color: TOKENS.ink2,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a8 8 0 0 1-12 7l-5 1 1-5a8 8 0 1 1 16-3z" />
          </svg>
          <span style={{ color: TOKENS.muted, fontWeight: 600 }}>{comments.length}</span>
        </button>

        <div className="flex-1" />

        <button
          ref={shareBtnRef}
          type="button"
          aria-label="Share"
          onClick={() => setShareMenuOpen((v) => !v)}
          className={`inline-flex items-center justify-center hover:bg-[#F8FAFD] ${shareMenuOpen ? "bg-[#F8FAFD]" : "bg-transparent"}`}
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "10px",
            border: "none",
            cursor: "pointer",
            color: shareMenuOpen ? TOKENS.ink : TOKENS.muted,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>

        <ShareSheet
          open={shareMenuOpen}
          onClose={() => setShareMenuOpen(false)}
          anchorEl={shareBtnRef.current}
          title="Share this sign"
          actions={[
            {
              key: "fb",
              label: "Facebook",
              listLabel: "Share to Facebook",
              onClick: handleShareFacebook,
              bg: "#1877F2",
              icon: <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88V14.9H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.9h-2.33v6.98C18.34 21.13 22 16.99 22 12z"/></svg>,
            },
            {
              key: "li",
              label: "LinkedIn",
              listLabel: "Share to LinkedIn",
              onClick: handleShareLinkedIn,
              bg: "#0A66C2",
              icon: <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM8.34 17H5.67V9.67h2.67V17zM7 8.34a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zM18.34 17h-2.67v-3.83c0-1 0-2.16-1.32-2.16-1.32 0-1.52 1-1.52 2.1V17h-2.67V9.67h2.56v1h.04a2.81 2.81 0 0 1 2.53-1.39c2.7 0 3.21 1.78 3.21 4.1V17z"/></svg>,
            },
            {
              key: "copy",
              label: "Copy link",
              onClick: handleCopyLink,
              bg: TOKENS.surface2,
              color: TOKENS.ink2,
              icon: (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              ),
            },
            {
              key: "download",
              label: "Download",
              listLabel: "Download as image",
              onClick: handleDownload,
              bg: TOKENS.surface2,
              color: TOKENS.ink2,
              icon: (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              ),
            },
          ]}
        />
      </div>

      {/* inline comment thread */}
      {showComments && (
        <div style={{ borderTop: `1px solid ${TOKENS.hairline}`, background: TOKENS.surface2, padding: "12px 22px 16px" }}>
          <CommentList
            jobId={group.postId}
            comments={comments}
            currentUser={currentUser}
            onAddComment={onAddComment}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
            onToggleFavorite={onReactToggle}
          />
        </div>
      )}

      {/* hidden share buttons (clicked programmatically) */}
      <div style={{ display: "none" }}>
        {shareImg && (
          <FacebookShareButton url={shareImg} ref={fbBtnRef}>
            <span>Facebook</span>
          </FacebookShareButton>
        )}
        {shareImg && (
          <LinkedinShareButton url={shareImg} ref={liBtnRef}>
            <span>LinkedIn</span>
          </LinkedinShareButton>
        )}
        {shareImg && (
          <a ref={dlBtnRef} href={shareImg} download={`yard-sign-${group.postId}.jpg`} target="_blank" rel="noopener noreferrer">
            Download
          </a>
        )}
      </div>

      {/* lightbox — rendered via portal so it truly covers the viewport
          instead of being clipped/shrunk by the feed's scrolling ancestors */}
      {active && createPortal(
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-[3px]" onClick={() => setLightboxIdx(null)}>
          <div
            className="w-full max-w-[400px] md:max-w-[560px] lg:max-w-[640px] max-h-full bg-slate-900 rounded-[20px] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 24px 48px -8px rgba(16,24,40,.5)" }}
          >
            <div className="relative flex-shrink-0 group">
              <div
                ref={lightboxTrackRef}
                onScroll={onLightboxTrackScroll}
                // Fixed aspect ratio (matching the main card's slides) instead
                // of sizing off the <img>'s natural dimensions — before an
                // image finishes loading it has no intrinsic size, so the
                // whole track (and every absolutely-positioned control inside
                // it: close button, arrows, dots) collapsed to a sliver at
                // the top of the card until the photo popped in.
                className="w-full flex overflow-x-auto snap-x snap-mandatory aspect-[4/3] bg-slate-800"
                style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }}
              >
                {group.signs.map((sign) => (
                  <img
                    key={sign.id}
                    src={sign.img}
                    alt="Yard sign"
                    className="w-full h-full flex-shrink-0 object-cover block"
                    style={{ scrollSnapAlign: "start" }}
                  />
                ))}
              </div>
              <span className="absolute top-[12px] left-[12px] px-[10px] py-[5px] rounded-[9px] text-white text-[11px] font-semibold flex items-center gap-[5px]" style={{ background: "rgba(15,23,42,.5)", backdropFilter: "blur(6px)" }}>
                <Fence size={13} /> Yard sign
              </span>
              <button type="button" onClick={() => setLightboxIdx(null)} aria-label="Close" className="absolute top-[12px] right-[12px] w-[32px] h-[32px] rounded-[9px] text-white flex items-center justify-center" style={{ background: "rgba(15,23,42,.5)", backdropFilter: "blur(6px)" }}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

              {/* Prev/next — the track only had scroll-snap (swipe/drag) to
                  navigate by, which a desktop mouse user simply can't do. */}
              {group.signs.length > 1 && lightboxIdx > 0 && (
                <button
                  type="button"
                  aria-label="Previous sign"
                  onClick={() => goToLightboxPhoto(-1)}
                  className="absolute top-1/2 left-[12px] -translate-y-1/2 flex items-center justify-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(15,23,42,.5)",
                    backdropFilter: "blur(6px)",
                    color: "#fff",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
              )}
              {group.signs.length > 1 && lightboxIdx < group.signs.length - 1 && (
                <button
                  type="button"
                  aria-label="Next sign"
                  onClick={() => goToLightboxPhoto(1)}
                  className="absolute top-1/2 right-[12px] -translate-y-1/2 flex items-center justify-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(15,23,42,.5)",
                    backdropFilter: "blur(6px)",
                    color: "#fff",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              )}

              {group.signs.length > 1 && (
                <div className="absolute bottom-[12px] left-1/2 -translate-x-1/2 flex items-center gap-[5px]">
                  {group.signs.map((sign, i) => (
                    <span
                      key={sign.id}
                      className="rounded-full transition-[width] duration-200"
                      style={{
                        width: i === lightboxIdx ? 14 : 6,
                        height: 6,
                        background: i === lightboxIdx ? "#fff" : "rgba(255,255,255,0.55)",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white px-[16px] pt-[14px] pb-[16px] flex-shrink-0 overflow-y-auto">
              <div className="text-[15px] font-semibold text-slate-900 leading-snug">{active.address || active.area}</div>
              <div className="flex items-center gap-[8px] mt-[10px]">
                {group.avatar ? (
                  <img src={group.avatar} alt={group.name} className="w-[26px] h-[26px] rounded-full object-cover flex-shrink-0" />
                ) : (
                  <span className="w-[26px] h-[26px] rounded-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">{group.initials}</span>
                )}
                <span className="text-[12.5px] text-slate-600 font-medium flex-grow truncate">{group.name}</span>
                {active.points > 0 && (
                  <span className="px-[8px] py-[3px] rounded-full text-[11px] font-semibold text-emerald-600 bg-emerald-50 flex-shrink-0">+{fmtPts(active.points)} pts</span>
                )}
              </div>
              <div className="text-[12px] text-slate-400 font-medium mt-[8px]">Placed {active.when}</div>
              <a href={mapsUrl(active.lat, active.lng, active.address)} target="_blank" rel="noreferrer" className="block text-center mt-[14px] py-[9px] rounded-[10px] text-[12.5px] font-medium text-white" style={{ background: brand }}>
                View on map
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
